import {
  AttachmentFile,
  Id,
  Message,
  MessageContent,
  ReplyToMessageContent,
} from '@nextforce/api'
import { t } from 'app/i18l'
import { isImageType, isVideoType } from 'file_folder_repository/util'
import { createContext, useCallback, useContext, useState } from 'react'
import { replaceMentionValues } from 'react-native-controlled-mentions'
import { errorAlert } from 'shared/alerts'
import { useCreateComposeMessage } from './compose/queries/create'
import { ComposeMessageEvent } from './compose/queries/shared/types'
import { useChatConfig } from './config/queries/get'
import { useCreateMessage } from './messages/queries/create'
import { useCreateFastUploadMessage } from './messages/queries/create_fast_upload'
import { AccessoryButtonName } from './messages/views/accessory_bar'
import {
  addMessageAttachment,
  editMessageAttachment,
  removeMessageAttachment,
} from './shared/util'

export type FileUploadStatus =
  | {
      error?: Error
      progress?: number
    }
  | undefined

interface onSendComposeMessageProps {
  chatGroupId: string
  groupIds: number[]
  userIds: number[]
  storeId: Id | undefined
  isReadOnly: boolean
  isUrgent: boolean
}

interface SendMessageContextValue {
  getMessageBody: (chatGroupId: string) => string
  setMessageBody: (chatGroupId: string, body: string) => void
  getSelectedAttachments: (chatGroupId: string) => AttachmentFile[]
  addAttachment: (
    chatGroupId: string,
    type: AccessoryButtonName
  ) => Promise<void>
  removeAttachment: (chatGroupId: string, uri: string) => void
  editAttachment: (chatGroupId: string, uri: string) => Promise<void>
  getFileUploadStatus: (chatGroupId: string) => FileUploadStatus
  setFileUploadStatus: (chatGroupId: string, status: FileUploadStatus) => void
  clearSendMessageData: (chatGroupId: string) => void
  onSendComposeMessage: ({
    chatGroupId,
    storeId,
    isReadOnly,
    isUrgent,
    groupIds,
    userIds,
  }: onSendComposeMessageProps) => Promise<ComposeMessageEvent | undefined>
  onSendMessage: ({
    conversationId,
  }: {
    conversationId: string
  }) => Promise<void>
  clearAttachments: (chatGroupId: string) => void
  pasteAttachments: (chatGroupId: string, files: AttachmentFile[]) => void
  getReplyMessage: (chatGroupId: string) => Message | undefined
  setReplyMessage: (chatGroupId: string, message: Message | undefined) => void
}

const SendMessageContext = createContext<SendMessageContextValue>(
  {} as SendMessageContextValue
)

export function useSendMessageContext() {
  return useContext(SendMessageContext)
}

export function SendMessageProvider({ children }: any) {
  const { data: chatConfig } = useChatConfig()
  const [messageBodies, setMessageBodies] = useState<Record<string, string>>({})
  const [replyMessages, setReplyMessages] = useState<
    Record<string, Message | undefined>
  >({})
  const [selectedAttachments, setSelectedAttachments] = useState<
    Record<string, AttachmentFile[]>
  >({})
  const [fileUploadStatus, setFileUploadStatusState] = useState<
    Record<string, FileUploadStatus>
  >({})
  const createComposeMessageMutation = useCreateComposeMessage()
  const createMessageMutation = useCreateMessage()
  const createFastUploadMessageMutation = useCreateFastUploadMessage()

  const getMessageBody = useCallback(
    (chatGroupId: string) => {
      return messageBodies[chatGroupId] || ''
    },
    [messageBodies]
  )

  const getSelectedAttachments = useCallback(
    (chatGroupId: string) => {
      return selectedAttachments[chatGroupId] || []
    },
    [selectedAttachments]
  )

  const getFileUploadStatus = useCallback(
    (chatGroupId: string) => {
      return fileUploadStatus[chatGroupId] || undefined
    },
    [fileUploadStatus]
  )

  const setFileUploadStatus = useCallback(
    (chatGroupId: string, value: FileUploadStatus) => {
      setFileUploadStatusState(prevState => ({
        ...prevState,
        [chatGroupId]: {
          error: value?.error,
          progress: value
            ? value.progress ?? prevState[chatGroupId]?.progress
            : undefined,
        },
      }))
    },
    []
  )

  const getReplyMessage = useCallback(
    (chatGroupId: string) => {
      return replyMessages[chatGroupId]
    },
    [replyMessages]
  )

  const setReplyMessage = useCallback(
    (chatGroupId: string, message: Message | undefined) => {
      setReplyMessages(prevState => ({
        ...prevState,
        [chatGroupId]: message,
      }))
    },
    []
  )

  const clearAttachments = useCallback((chatGroupId: string) => {
    setSelectedAttachments(prevState => ({
      ...prevState,
      [chatGroupId]: [],
    }))
  }, [])

  const clearMessageBody = useCallback((chatGroupId: string) => {
    setMessageBodies(prevState => ({
      ...prevState,
      [chatGroupId]: '',
    }))
  }, [])

  const clearSendMessageData = useCallback(
    (chatGroupId: string) => {
      clearMessageBody(chatGroupId)
      clearAttachments(chatGroupId)
    },
    [clearAttachments, clearMessageBody]
  )

  const removeAttachment = useCallback(
    (chatGroupId: string, uri: string) =>
      removeMessageAttachment({ chatGroupId, uri, setSelectedAttachments }),
    []
  )

  const onSendComposeMessage = useCallback(
    async ({
      chatGroupId,
      storeId,
      isReadOnly,
      groupIds,
      userIds,
      isUrgent,
    }: onSendComposeMessageProps) => {
      const messageBody = messageBodies[chatGroupId]
      const attachments = selectedAttachments[chatGroupId] || []
      const totalItemsToSend = attachments.length > 0 ? attachments.length : 1
      let finalComposeMessageEvent: ComposeMessageEvent | undefined
      let totalItemsSent = 0

      if (attachments.length > 0) {
        setFileUploadStatus(chatGroupId, {
          progress: 0,
        })
      }

      for (let i = 0; i < totalItemsToSend; i++) {
        let messageContent: MessageContent = {}
        if (i === 0) {
          messageContent = { body: messageBody }
        }
        if (i < attachments.length) {
          messageContent = { ...messageContent, attachment: attachments[i] }
        }

        try {
          finalComposeMessageEvent =
            await createComposeMessageMutation.mutateAsync({
              groupIds,
              userIds,
              content: messageContent,
              storeId,
              isUrgent,
              messageManagers: isReadOnly,
              setFileUploadStatus: value =>
                attachments.length > 0
                  ? setFileUploadStatus(chatGroupId, {
                      progress:
                        (totalItemsSent + (value?.progress ?? 0)) /
                        totalItemsToSend,
                    })
                  : undefined,
            })
          totalItemsSent++
          clearSendMessageData(chatGroupId)
        } catch (error) {
          errorAlert(error, t('sendMessageError'))
          throw error
        } finally {
          setFileUploadStatus(chatGroupId, undefined)
        }
      }
      return finalComposeMessageEvent
    },
    [
      messageBodies,
      selectedAttachments,
      clearSendMessageData,
      setFileUploadStatus,
      createComposeMessageMutation,
    ]
  )

  const onSendMessage = useCallback(
    async ({ conversationId }: { conversationId: string }) => {
      const messageBody = getMessageBody(conversationId)
      const attachments = getSelectedAttachments(conversationId)
      const message = getReplyMessage(conversationId)

      const replyToMessage: ReplyToMessageContent | undefined = message
        ? {
            userId: message.user.id,
            id: message.id,
            chatGroupId: message.conversationId,
            attachment: message.attachment
              ? {
                  thumbnail: message.attachment.thumbnail,
                  mime_type: message.attachment.mimeType,
                  url: message.attachment.uri,
                  caption: message.attachment.caption,
                  name: message.attachment.name,
                }
              : undefined,
            body: message.body,
          }
        : undefined

      const totalItemsToSend = attachments.length > 0 ? attachments.length : 1
      let totalItemsSent = 0

      if (attachments.length > 0) {
        setFileUploadStatus(conversationId, {
          progress: 0,
        })
      }

      for (let i = 0; i < totalItemsToSend; i++) {
        let messageContent: MessageContent = {}
        if (i === 0) {
          const { mentionedUserIds, body } = parseMessageBody(messageBody)
          messageContent = {
            body,
            replyToMessageContent: replyToMessage,
            mentionedUserIds,
          }
        }
        if (i < attachments.length) {
          messageContent = { ...messageContent, attachment: attachments[i] }
        }

        const shouldUseFastUploadMessage =
          messageContent.attachment &&
          (isImageType(messageContent.attachment.mimeType) ||
            isVideoType(messageContent.attachment.mimeType))

        try {
          if (shouldUseFastUploadMessage) {
            await createFastUploadMessageMutation.mutateAsync({
              conversationId,
              content: messageContent,
              setFileUploadStatus: value =>
                attachments.length > 0
                  ? setFileUploadStatus(conversationId, {
                      progress:
                        (totalItemsSent + (value?.progress ?? 0)) /
                        totalItemsToSend,
                    })
                  : undefined,
              isFileSharing: false,
            })
          } else {
            await createMessageMutation.mutateAsync({
              conversationId,
              content: messageContent,
              setFileUploadStatus: value =>
                attachments.length > 0
                  ? setFileUploadStatus(conversationId, {
                      progress:
                        (totalItemsSent + (value?.progress ?? 0)) /
                        totalItemsToSend,
                    })
                  : undefined,
            })
          }
          totalItemsSent++

          if (i === 0) {
            clearMessageBody(conversationId)
          }
          if (i < attachments.length) {
            removeAttachment(conversationId, attachments[i].uri)
          }
        } catch (error) {
          errorAlert(error, t('sendMessageError'))
          setMessageBodies(prevState => ({
            ...prevState,
            [conversationId]: messageBody,
          }))
        }
      }
      setFileUploadStatus(conversationId, undefined)
      if (replyToMessage) {
        setReplyMessage(conversationId, undefined)
      }
    },
    [
      clearMessageBody,
      createFastUploadMessageMutation,
      createMessageMutation,
      getMessageBody,
      getReplyMessage,
      getSelectedAttachments,
      removeAttachment,
      setFileUploadStatus,
      setReplyMessage,
    ]
  )

  const addAttachment = useCallback(
    async (chatGroupId: string, type: AccessoryButtonName) =>
      addMessageAttachment({
        chatGroupId,
        type,
        chatConfig,
        setSelectedAttachments,
      }),
    [chatConfig]
  )

  const pasteAttachments = useCallback(
    async (chatGroupId: string, files: AttachmentFile[]) => {
      setSelectedAttachments(prevState => ({
        ...prevState,
        [chatGroupId]: prevState[chatGroupId]
          ? [...prevState[chatGroupId], ...files]
          : files,
      }))
    },
    []
  )

  const editAttachment = useCallback(
    async (chatGroupId: string, uri: string) =>
      editMessageAttachment({
        chatGroupId,
        uri,
        selectedAttachments,
        setSelectedAttachments,
      }),
    [selectedAttachments]
  )

  return (
    <SendMessageContext.Provider
      value={{
        getMessageBody,
        getSelectedAttachments,
        setMessageBody: (chatGroupId, body) =>
          setMessageBodies(prevState => ({
            ...prevState,
            [chatGroupId]: body,
          })),
        addAttachment,
        editAttachment,
        removeAttachment,
        getFileUploadStatus,
        setFileUploadStatus,
        clearSendMessageData,
        onSendComposeMessage,
        onSendMessage,
        clearAttachments,
        pasteAttachments,
        getReplyMessage,
        setReplyMessage,
      }}
    >
      {children}
    </SendMessageContext.Provider>
  )
}

function parseMessageBody(messageBody: string): {
  mentionedUserIds: Id[]
  body: string
} {
  const mentionedUserIds: Id[] = []
  const parsedBody = replaceMentionValues(messageBody, ({ id, name }) => {
    mentionedUserIds.push(id)
    return `@${name}`
  })
  return { mentionedUserIds, body: parsedBody }
}